// src/components/CartDrawer.js
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { LanguageContext } from "../context/LanguageContext";
import { apiRequest } from "../services/api";

function CartDrawer() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const {
  isDrawerOpen,
  closeDrawer,
  cartItems,
  subtotal,
  discount,
  total,
  bxgySuggestions,
  addBxgyDiscountItem,
} = useCart();

  const t = {
    EN: {
      title: "Your Cart",
      viewCart: "View Full Cart",
      checkout: "Checkout",
      empty: "Your bag is empty.",
      addDiscount: "Add & get",
      suggestionTitle: "Special offer for you",
      suggestionText: "You can add this item and get",
    },
    AR: {
      title: "سلة المشتريات",
      viewCart: "عرض السلة كاملة",
      checkout: "إتمام الشراء",
      empty: "سلتك فارغة.",
      suggestionTitle: "عرض خاص لك",
      addDiscount: "أضف واحصل على",
      suggestionText: "يمكنك إضافة هذا المنتج والحصول على خصم",
    },
  }[lang];

  const handleViewCart = () => {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "open_cart_page",
        entity_type: "cart",
      }),
    }).catch(() => {});

    closeDrawer();
    navigate("/cart");
  };

  const freeItems = cartItems.filter((item) => item.isBxgyFree);

const normalItems = cartItems.filter(
  (item) => !item.isBxgyFree && !item.isBxgyDiscounted
);

const discountedItems = cartItems.filter(
  (item) => item.isBxgyDiscounted
);
  return (
    <div
      className={`
        fixed inset-0 z-[9999]
        transition-opacity duration-300
        ${
          isDrawerOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }
      `}
    >
      {/* BACKDROP */}
      <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} />

      {/* PANEL */}
      <div
        className={`
          absolute top-0 right-0 h-full
          w-[82%] sm:w-[380px] md:w-[420px]
          bg-white
          shadow-[0_20px_60px_rgba(0,0,0,0.18)]
          transform transition-transform duration-300
          ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}
          rounded-l-3xl overflow-hidden
        `}
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        {/* HEADER */}
        <div
          className="
            px-5 py-4
            flex items-center justify-between
            border-b border-white/30
            text-white
          "
          style={{
            background:
              "linear-gradient(135deg,#7C3AED,#2563EB,#22C55E,#F59E0B,#EF4444)",
            backgroundSize: "220% 220%",
            animation: "toyGradient 10s ease-in-out infinite",
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">🧸</span>
            <h3 className="text-base font-extrabold tracking-wide">
              {t.title}
            </h3>
          </div>

          <button
            onClick={closeDrawer}
            className="text-2xl text-white/90 hover:text-white transition"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex flex-col h-[calc(100%-64px)] bg-[#FAFAFF]">
          {/* ITEMS */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {cartItems.length === 0 && (
              <div className="mt-10 text-center">
                <div className="text-4xl mb-3">🛒</div>
                <p className="text-sm text-gray-500">{t.empty}</p>
              </div>
            )}

            {/* NORMAL ITEMS */}
{normalItems.map((item) => (
  <div
    key={item.id}
    className="flex gap-3 bg-white rounded-2xl p-3 border border-gray-100 shadow-[0_10px_25px_rgba(0,0,0,0.05)]"
  >
    <img
      src={item.image}
      alt={lang === "EN" ? item.nameEN : item.nameAR}
      className="w-16 h-16 object-cover rounded-xl bg-gray-100"
    />

    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-gray-800 truncate">
        {lang === "EN" ? item.nameEN : item.nameAR}
      </p>

      <div className="mt-1 flex items-center gap-2">
        <span
          className="text-[11px] font-extrabold px-2 py-[2px] rounded-full text-white"
          style={{
            background: "linear-gradient(90deg,#F97316,#EF4444)",
          }}
        >
          {lang === "EN" ? `QTY ${item.qty}` : `الكمية ${item.qty}`}
        </span>

        <span className="text-sm font-extrabold text-gray-900">
          ${item.price}
        </span>
      </div>
    </div>
  </div>
))}

{/* DISCOUNTED ITEMS */}
{discountedItems.length > 0 && (
  <div className="mt-4">
    <p className="text-xs font-bold text-purple-600 mb-2">
      🎯 {lang === "EN" ? "Discounted Items" : "منتجات مخفضة"}
    </p>

    {discountedItems.map((item) => (
      <div
        key={item.id}
        className="flex gap-3 bg-purple-50 rounded-2xl p-3 border border-purple-200 shadow-[0_10px_25px_rgba(0,0,0,0.05)]"
      >
        <img
          src={item.image}
          alt={lang === "EN" ? item.nameEN : item.nameAR}
          className="w-16 h-16 object-cover rounded-xl bg-gray-100"
        />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">
            {lang === "EN" ? item.nameEN : item.nameAR}
          </p>

          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm font-extrabold text-gray-900">
              ${item.price}
            </span>

            <span
              className="text-[10px] font-extrabold px-2 py-[2px] rounded-full text-white"
              style={{
                background: "linear-gradient(90deg,#9333EA,#7C3AED)",
              }}
            >
              -{item.bxgyDiscountValue}% 🎯
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
)}

{bxgySuggestions?.length > 0 && (
  <div className="mt-4">
    <p className="text-xs font-bold text-purple-600 mb-2">
      🎯 {t.suggestionTitle}
    </p>

    {bxgySuggestions.map((item, idx) => (
      <div
        key={`${item.buyProductId}-${item.getProductId}-${idx}`}
        className="
          flex gap-3
          bg-purple-50
          rounded-2xl
          p-3
          border border-purple-200
          shadow-[0_10px_25px_rgba(0,0,0,0.05)]
        "
      >
        <img
          src={item.image}
          alt={lang === "EN" ? item.nameEN : item.nameAR}
          className="w-16 h-16 object-cover rounded-xl bg-gray-100"
        />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">
            {lang === "EN" ? item.nameEN : item.nameAR}
          </p>

          <div className="mt-1 text-[12px] text-gray-600 leading-5">
            {t.suggestionText}{" "}
            <span className="font-extrabold text-purple-700">
              {item.discountValue}% OFF
            </span>
          </div>
          <button
  onClick={() => addBxgyDiscountItem(item)}
  className="
    mt-2
    text-[11px]
    px-3 py-1
    rounded-full
    font-extrabold
    text-white
    shadow-sm
    transition active:scale-[0.96]
  "
  style={{
    background:
      "linear-gradient(90deg,#9333EA,#7C3AED,#6366F1)",
  }}
>
  {t.addDiscount} {item.discountValue}% OFF ✨
</button>
        </div>
      </div>
    ))}
  </div>
)}

{/* FREE ITEMS */}
{freeItems.length > 0 && (
  <div className="mt-4">
    <p className="text-xs font-bold text-green-600 mb-2">
      🎁 {lang === "EN" ? "Free Gifts" : "هدايا مجانية"}
    </p>

    {freeItems.map((item) => (
      <div
        key={item.id}
        className="flex gap-3 bg-green-50 rounded-2xl p-3 border border-green-200 shadow-[0_10px_25px_rgba(0,0,0,0.05)]"
      >
        <img
          src={item.image}
          alt={lang === "EN" ? item.nameEN : item.nameAR}
          className="w-16 h-16 object-cover rounded-xl bg-gray-100"
        />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">
            {lang === "EN" ? item.nameEN : item.nameAR}
          </p>

          <div className="mt-1 flex items-center gap-2">
            <span
              className="text-[11px] font-extrabold px-2 py-[2px] rounded-full text-white"
              style={{
                background: "linear-gradient(90deg,#22C55E,#16A34A)",
              }}
            >
              FREE 🎁
            </span>

            <span className="text-sm font-extrabold text-green-600">
              $0
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
)}
          </div>

          {/* SUMMARY + ACTIONS */}
          {cartItems.length > 0 && (
            <div className="px-5 pb-5 pt-4">
              <div
                className="
                  rounded-2xl p-4
                  bg-white
                  border border-gray-100
                  shadow-[0_16px_40px_rgba(0,0,0,0.08)]
                "
              >
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-semibold">
                    Subtotal
                  </span>
                  <span className="font-extrabold text-gray-900">
                    ${subtotal}
                  </span>
                </div>

                {discount > 0 && (
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-gray-500 font-semibold">
                      Discount
                    </span>
                    <span className="text-green-600 font-extrabold">
                      - ${discount}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-sm font-extrabold mt-3">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">${total}</span>
                </div>

                <button
                  onClick={handleViewCart}
                  className="
                    w-full mt-4 py-3
                    rounded-xl
                    text-sm font-extrabold
                    border
                    transition active:scale-[0.99]
                  "
                  style={{
                    borderColor: "#111827",
                    background:
                      "linear-gradient(90deg,#FFFFFF,#F3F4F6)",
                    color: "#111827",
                  }}
                >
                  {t.viewCart}
                </button>

                <button
                  onClick={() => {
                    apiRequest("/analytics/track", {
                      method: "POST",
                      body: JSON.stringify({
                        action_type: "enter_checkout",
                        entity_type: "cart",
                      }),
                    }).catch(() => {});

                    closeDrawer();
                    navigate("/checkout");
                  }}
                  className="
                    w-full mt-3 py-3
                    rounded-xl
                    text-sm font-extrabold text-white
                    shadow-[0_18px_45px_rgba(0,0,0,0.12)]
                    transition active:scale-[0.99]
                  "
                  style={{
                    background:
                      "linear-gradient(90deg,#22C55E,#06B6D4,#2563EB)",
                  }}
                >
                  {t.checkout} ✨
                </button>
              </div>
            </div>
          )}
        </div>

        {/* LOCAL ANIMATION (design only) */}
        <style>{`
          @keyframes toyGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default CartDrawer;